import React from 'react'
import {
  isHighSchoolUrl,
  isK5Url,
  isMiddleSchoolUrl,
} from 'utils/routing/routeHelpers'
import { selectors as lmsSelectors } from 'store/lmsManager'

export const getCompletedTrainingProgramNodesCount = data => {
  const trainingProgramNodes =
    data?.educator?.lesson_nodes?.filter(
      node =>
        node?.lesson_interaction_type === 'trainingProgram' && node?.is_done,
    ) || []

  return trainingProgramNodes.length
}

/**
 * Gets the appropriate instructor data based on the current URL context
 * @param {Object} state - Redux state
 * @returns {Object} The appropriate instructor data for the current program
 */
export const getInstructorDataForCurrentProgram = state => {
  if (isHighSchoolUrl()) {
    return lmsSelectors.selectHsInstructorData(state)
  } else if (isMiddleSchoolUrl()) {
    return lmsSelectors.selectMiddleSchoolInstructorData(state)
  } else if (isK5Url()) {
    return lmsSelectors.selectk5InstructorData(state)
  }
  return null
}

/**
 * Gets the total number of training leaf nodes in a course tree
 * @param {Object} courseTree - The course tree to analyze
 * @returns {number} The total number of required training leaf nodes
 */
export const getTotalTrainingNodesCount = courseTree => {
  if (!courseTree) {
    return 0
  }

  const leafNodes = []

  // Recursive function to traverse the course tree and collect leaf nodes
  const findLeafNodes = node => {
    if (!node) return

    // If this is a leaf node, add it to our collection
    if (node?.leaf === true) {
      leafNodes.push(node)
    }

    // If the node has children, recursively process them
    if (node?.children && node?.children?.length > 0) {
      for (const child of node?.children || []) {
        findLeafNodes(child)
      }
    }
  }

  // Start the recursive search from the root of the course tree
  findLeafNodes(courseTree)

  // Filter out optional lessons (choice requirements)
  const requiredLeafNodes = leafNodes.filter(
    node => node?.requirementType !== 'choice',
  )

  return requiredLeafNodes.length
}

/**
 * Gets the number of completed training nodes for K8 programs
 * @param {Object} instructorData - The instructor data from lmsManager Redux
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {number} The number of completed training nodes
 */
export const getCompletedK8TrainingNodesCount = (
  instructorData = {},
  isK5Product = false,
  isMsProduct = false,
) => {
  if (!instructorData?.educator) {
    return 0
  }

  let completedCount = 0

  // Handle the case where educator is an array of registrations
  if (Array.isArray(instructorData?.educator)) {
    // Create a set to track unique completed nodes (avoid double counting)
    const completedNodes = new Set()
    // Iterate through each registration in the educator array
    for (const registration of instructorData?.educator || []) {
      // Check each registration's lesson_nodes if it exists
      for (const lesson of registration?.lesson_nodes || []) {
        if (lesson && lesson?.is_done === true) {
          // Extract the course node ID (remove presentation ID if present)
          const courseNodeId = lesson?.leaf_node_id?.split('-')?.[0]
          if (courseNodeId) {
            completedNodes.add(courseNodeId)
          }
        }
      }
    }

    completedCount = completedNodes.size
  }

  return completedCount
}

/**
 * Formats the training progress string based on the program type
 * @param {number} completedCount - Number of completed training nodes
 * @param {number} totalCount - Total number of training nodes (for K8 only)
 * @param {boolean} isHighSchool - Whether this is high school program
 * @returns {string} The formatted training progress string
 */
export const formatTrainingProgressString = (
  completedCount,
  totalCount = 0,
  isHighSchool = false,
) => {
  if (isHighSchool) {
    return `${completedCount}`
  } else {
    return `${completedCount}/${totalCount} `
  }
}

/**
 * Gets the appropriate course tree name for training based on program type
 * @param {boolean} isK5Product - Whether the current product is K5
 * @param {boolean} isMsProduct - Whether the current product is Middle School
 * @returns {string|null} The course tree name for training or null if not K8
 */
export const getTrainingCourseTreeName = (
  isK5Product = false,
  isMsProduct = false,
) => {
  if (isK5Product) {
    return 'k-5_training_tier1'
  } else if (isMsProduct) {
    return '6-8_training_tier1'
  }
  return null
}

/**
 * Determines if the MarkComplete component should be hidden based on missing programTrack
 * @param {Object} userContextManager - The raw user context manager state
 * @param {boolean} isK5Url - Whether the current URL is for K5/elementary
 * @param {boolean} isMiddleSchoolUrl - Whether the current URL is for middle school
 * @returns {boolean} True if the component should be hidden, false otherwise
 */
export function shouldHideMarkCompleteForMissingProgramTrack(
  userContextManager,
  isK5Url,
  isMiddleSchoolUrl,
) {
  // Only apply logic for K5 or Middle School URLs
  if (!isK5Url && !isMiddleSchoolUrl) {
    return false
  }

  // Get the appropriate active site ID based on the URL type
  const activeSiteId = isK5Url
    ? userContextManager?.elementaryActiveSiteId
    : userContextManager?.middleSchoolActiveSiteId

  // Return false (don't hide) if no active site ID or no sites data
  if (!activeSiteId || !userContextManager?.sites) {
    return false
  }

  // Find the active site
  const activeSite = userContextManager.sites.find(
    site => site.siteId === activeSiteId,
  )

  if (!activeSite) {
    return false
  }

  // Determine the program key based on URL type
  const programKey = isK5Url ? 'k5SelDigital' : 'mspDigital'
  const program = activeSite.programs?.[programKey]

  // Hide component if programTrack is null, undefined, or missing
  return !program?.preferences?.programTrack
}

/**
 * Checks if the current pathname matches the training path pattern
 * @param {string} pathname - The pathname to check (defaults to window.location.pathname)
 * @returns {boolean} True if pathname matches product/<program>/trainings/<training> pattern
 */
export function matchesTrainingPathPattern(
  pathname = window.location.pathname,
) {
  const pattern = /^\/product\/[^/]+\/trainings\/[^/]+/
  return pattern.test(pathname)
}

export function manageContent(
  content,
  ContentComponents,
  getContentComponent,
  callback,
) {
  if (!content || content.length <= 0) {
    return null
  }

  const contentComponents = []
  let inAccordion = false
  let accordionContent = []
  let accordionData = {}
  for (const child of content) {
    const { name, data } = child
    const ContentComponent = ContentComponents[name]

    if (name === 'accordion' && !inAccordion) {
      inAccordion = true
      accordionData = data
      accordionContent = []
      continue
    }
    if (name === 'accordion' && inAccordion) {
      inAccordion = false
      contentComponents.push(
        <ContentComponent
          key={`content-${contentComponents.length}-${name}`}
          {...accordionData}
        >
          {accordionContent}
        </ContentComponent>,
      )
      continue
    }
    if (inAccordion) {
      accordionContent.push(getContentComponent(child))
      continue
    }
    contentComponents.push(getContentComponent(child, contentComponents.length))
  }
  return callback(contentComponents)
}
